// Operaciones inmutables sobre el diseño del correo (filas → columnas → bloques).
import { type Block, type BlockType, cloneBlock, cloneRow, type EmailDesign, newBlock, newRow, type Row } from "./types.ts";

const mapRows = (d: EmailDesign, fn: (r: Row) => Row): EmailDesign => ({ ...d, rows: d.rows.map(fn) });
const mapBlocks = (d: EmailDesign, fn: (blocks: Block[]) => Block[]): EmailDesign =>
  mapRows(d, (r) => ({ ...r, columns: r.columns.map((c) => ({ ...c, blocks: fn(c.blocks) })) }));

export function findBlock(d: EmailDesign, id: string): Block | null {
  for (const r of d.rows) for (const c of r.columns) for (const b of c.blocks) if (b.id === id) return b;
  return null;
}

/** Aplica cambios a un bloque; las claves con `undefined` se eliminan (vuelven al valor por defecto). */
export function updateBlock(d: EmailDesign, id: string, patch: Record<string, unknown>): EmailDesign {
  return mapBlocks(d, (bs) => bs.map((b) => {
    if (b.id !== id) return b;
    const next: Record<string, unknown> = { ...b, ...patch };
    for (const k of Object.keys(next)) if (next[k] === undefined) delete next[k];
    return next as unknown as Block;
  }));
}

export function removeBlock(d: EmailDesign, id: string): EmailDesign {
  return mapBlocks(d, (bs) => bs.filter((b) => b.id !== id));
}

export function duplicateBlock(d: EmailDesign, id: string): { design: EmailDesign; newId: string | null } {
  let newId: string | null = null;
  const design = mapBlocks(d, (bs) => bs.flatMap((b) => {
    if (b.id !== id) return [b];
    const copy = cloneBlock(b); newId = copy.id;
    return [b, copy];
  }));
  return { design, newId };
}

/** Mueve un bloque dentro de su columna; en los extremos salta a la fila anterior/siguiente. */
export function moveBlock(d: EmailDesign, id: string, dir: -1 | 1): EmailDesign {
  const rows = structuredClone(d.rows);
  for (let ri = 0; ri < rows.length; ri++) {
    const row = rows[ri]!;
    for (let ci = 0; ci < row.columns.length; ci++) {
      const blocks = row.columns[ci]!.blocks;
      const i = blocks.findIndex((b) => b.id === id);
      if (i < 0) continue;
      const j = i + dir;
      if (j >= 0 && j < blocks.length) {
        [blocks[i], blocks[j]] = [blocks[j]!, blocks[i]!];
      } else {
        const target = rows[ri + dir];
        if (!target) return d;
        const [b] = blocks.splice(i, 1);
        const col = target.columns[Math.min(ci, target.columns.length - 1)]!;
        if (dir < 0) col.blocks.push(b!); else col.blocks.unshift(b!);
      }
      return { ...d, rows };
    }
  }
  return d;
}

/** Añade un bloque en una columna (al final o en una posición) o en una fila nueva al final. */
export function addBlock(d: EmailDesign, type: BlockType, target: { rowId: string; colId: string; index?: number } | null): { design: EmailDesign; id: string } {
  const b = newBlock(type);
  if (!target) {
    const r = newRow([1]); r.columns[0]!.blocks.push(b);
    return { design: { ...d, rows: [...d.rows, r] }, id: b.id };
  }
  return {
    id: b.id,
    design: mapRows(d, (r) => r.id !== target.rowId ? r : {
      ...r,
      columns: r.columns.map((c) => {
        if (c.id !== target.colId) return c;
        const blocks = [...c.blocks];
        blocks.splice(target.index ?? blocks.length, 0, b);
        return { ...c, blocks };
      }),
    }),
  };
}

/** Lleva un bloque existente a otra columna/posición (arrastrar y soltar). */
export function relocateBlock(d: EmailDesign, id: string, target: { rowId: string; colId: string; index: number }): EmailDesign {
  const b = findBlock(d, id);
  if (!b) return d;
  // Ajusta el índice si el bloque estaba antes en la misma columna.
  let index = target.index;
  for (const r of d.rows) for (const c of r.columns) {
    const i = c.blocks.findIndex((x) => x.id === id);
    if (i >= 0 && r.id === target.rowId && c.id === target.colId && i < index) index -= 1;
  }
  const without = removeBlock(d, id);
  return mapRows(without, (r) => r.id !== target.rowId ? r : {
    ...r,
    columns: r.columns.map((c) => {
      if (c.id !== target.colId) return c;
      const blocks = [...c.blocks]; blocks.splice(index, 0, b);
      return { ...c, blocks };
    }),
  });
}

export const addRow = (d: EmailDesign, ratios: number[], afterId?: string): { design: EmailDesign; id: string } => {
  const r = newRow(ratios);
  const i = afterId ? d.rows.findIndex((x) => x.id === afterId) : -1;
  const rows = [...d.rows];
  rows.splice(i >= 0 ? i + 1 : rows.length, 0, r);
  return { design: { ...d, rows }, id: r.id };
};
export const updateRow = (d: EmailDesign, id: string, patch: Partial<Row>): EmailDesign => mapRows(d, (r) => (r.id === id ? { ...r, ...patch } : r));
export const removeRow = (d: EmailDesign, id: string): EmailDesign => ({ ...d, rows: d.rows.filter((r) => r.id !== id) });
export function moveRow(d: EmailDesign, id: string, dir: -1 | 1): EmailDesign {
  const i = d.rows.findIndex((r) => r.id === id); const j = i + dir;
  if (i < 0 || j < 0 || j >= d.rows.length) return d;
  const rows = [...d.rows]; [rows[i], rows[j]] = [rows[j]!, rows[i]!];
  return { ...d, rows };
}
export function duplicateRow(d: EmailDesign, id: string): EmailDesign {
  return { ...d, rows: d.rows.flatMap((r) => (r.id === id ? [r, cloneRow(r)] : [r])) };
}

/** Cambia la distribución de columnas; si se reducen, los bloques sobrantes pasan a la última columna. */
export function setRowLayout(d: EmailDesign, id: string, ratios: number[]): EmailDesign {
  return mapRows(d, (r) => {
    if (r.id !== id) return r;
    const fresh = newRow(ratios);
    const columns = fresh.columns.map((c, i) => r.columns[i] ?? c);
    if (r.columns.length > ratios.length) {
      const extra = r.columns.slice(ratios.length).flatMap((c) => c.blocks);
      const last = columns[columns.length - 1]!;
      columns[columns.length - 1] = { ...last, blocks: [...last.blocks, ...extra] };
    }
    return { ...r, ratios, columns };
  });
}
